import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMetaTags} from '../../services/useMetaTags';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import '../../assets/styles/search/searchPage.scss';

const TYPE_OPTIONS = [
    {value: 'all', labelKey: 'search.results.all'},
    {value: 'material', labelKey: 'search.results.library'},
    {value: 'conference', labelKey: 'search.results.conferences'},
    {value: 'course', labelKey: 'search.results.courses'},
    {value: 'testing', labelKey: 'search.results.testings'},
    {value: 'survey', labelKey: 'search.results.surveys'},
    {value: 'organization', labelKey: 'search.results.organizations'},
];

const SORT_OPTIONS = [
    {value: 'relevance', labelKey: 'search.sort.relevance'},
    {value: 'date_desc', labelKey: 'search.sort.newest'},
    {value: 'date_asc', labelKey: 'search.sort.oldest'},
];

const DEFAULT_QUERY = {
    search: '',
    type: 'all',
    page: 1,
    sort: 'relevance',
    dateFrom: '',
    dateTo: '',
    withPointsOrCertificate: false,
    onlineOnly: false,
    availableOnly: false,
};

function parseBooleanParam(value) {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
}

function parseSearchQuery(queryString) {
    const params = new URLSearchParams(queryString || '');

    return {
        search: params.get('search') || '',
        type: params.get('type') || 'all',
        page: Math.max(1, Number.parseInt(params.get('page') || '1', 10) || 1),
        sort: params.get('sort') || 'relevance',
        dateFrom: params.get('dateFrom') || '',
        dateTo: params.get('dateTo') || '',
        withPointsOrCertificate: parseBooleanParam(params.get('withPointsOrCertificate')),
        onlineOnly: parseBooleanParam(params.get('onlineOnly')),
        availableOnly: parseBooleanParam(params.get('availableOnly')),
    };
}

function buildSearchQuery(query) {
    const params = new URLSearchParams();

    if ((query.search || '').trim() !== '') {
        params.set('search', query.search.trim());
    }

    if (query.type && query.type !== 'all') {
        params.set('type', query.type);
    }

    if ((query.page || 1) > 1) {
        params.set('page', String(query.page));
    }

    if (query.sort && query.sort !== 'relevance') {
        params.set('sort', query.sort);
    }

    if (query.dateFrom) {
        params.set('dateFrom', query.dateFrom);
    }

    if (query.dateTo) {
        params.set('dateTo', query.dateTo);
    }

    if (query.withPointsOrCertificate) {
        params.set('withPointsOrCertificate', '1');
    }

    if (query.onlineOnly) {
        params.set('onlineOnly', '1');
    }

    if (query.availableOnly) {
        params.set('availableOnly', '1');
    }

    return params.toString();
}

function resolveEntityType(entity) {
    if (!entity?.type) {
        return 'material';
    }

    if (entity.type === 'course' && entity.isTesting) {
        return 'testing';
    }

    return entity.type;
}

function getEntityTypeLabel(type, t) {
    switch (type) {
        case 'survey':
            return t('search.list.survey');
        case 'material':
            return t('search.list.material');
        case 'course':
            return t('search.list.course');
        case 'testing':
            return t('search.list.testing', t('search.list.course'));
        case 'conference':
            return t('search.list.conference');
        case 'organization':
            return t('search.list.organization');
        default:
            return '';
    }
}

function resolveEntityUrl(entity) {
    const normalizedUrl = typeof entity?.url === 'string' ? entity.url.trim() : '';
    if (normalizedUrl !== '' && normalizedUrl !== '#') {
        return normalizedUrl;
    }

    return '';
}

function isInternalUrl(url) {
    return typeof url === 'string' && url.startsWith('/');
}

function getActionLabel(type, actionKey, t) {
    const prefix = `search.actions.${type}.${actionKey}`;
    const shared = `search.actions.shared.${actionKey}`;

    return t(prefix, t(shared, actionKey));
}

function getMetaItems(entity, t) {
    const type = resolveEntityType(entity);
    const meta = entity?.meta || {};

    switch (type) {
        case 'conference':
            return [
                meta.isOnline ? t('search.meta.online') : t('search.meta.offline'),
                meta.hasCertificate ? t('search.meta.withCertificate') : null,
                meta.registrationRequired ? t('search.meta.registrationRequired') : t('search.meta.registrationOptional'),
            ].filter(Boolean);
        case 'course':
        case 'testing':
            return [
                Number.isInteger(meta.modulesCount) ? t('search.meta.modulesCount', {count: meta.modulesCount}) : null,
                meta.hoursAmount ? t('search.meta.hoursAmount', {amount: meta.hoursAmount}) : null,
                meta.score ? t('search.meta.pointsAmount', {amount: meta.score}) : null,
                meta.hasCertificate ? t('search.meta.withCertificate') : null,
            ].filter(Boolean);
        case 'survey':
            return [
                Number.isInteger(meta.questionsCount) ? t('search.meta.questionsCount', {count: meta.questionsCount}) : null,
            ].filter(Boolean);
        case 'material':
            return [
                meta.hasTest ? t('search.meta.withTest') : null,
                meta.isChargeable ? t('search.meta.chargeable') : t('search.meta.free'),
                Array.isArray(meta.tags) && meta.tags.length > 0
                    ? t('search.meta.tags', {tags: meta.tags.join(', ')})
                    : null,
            ].filter(Boolean);
        case 'organization':
            return [
                Number.isInteger(meta.membersCount) ? t('search.meta.membersCount', {count: meta.membersCount}) : null,
            ].filter(Boolean);
        default:
            return [];
    }
}

function getPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items = [1];
    const from = Math.max(2, currentPage - 1);
    const to = Math.min(totalPages - 1, currentPage + 1);

    if (from > 2) {
        items.push('ellipsis-left');
    }

    for (let page = from; page <= to; page += 1) {
        items.push(page);
    }

    if (to < totalPages - 1) {
        items.push('ellipsis-right');
    }

    items.push(totalPages);

    return items;
}

function getEntityIcon(type) {
    switch (type) {
        case 'conference':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 2a1 1 0 011 1v1h8V3a1 1 0 112 0v1h1a2 2 0 012 2v14a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zm13 8H4v10h16V10z" fill="currentColor" />
                </svg>
            );
        case 'material':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 2h9l3 3v17a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zm8 1.5V6h2.5L14 3.5z" fill="currentColor" />
                </svg>
            );
        case 'course':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l10 5-10 5L2 7l10-5zm0 12l10-5v6l-10 5L2 15V9l10 5z" fill="currentColor" />
                </svg>
            );
        case 'testing':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 2h10a2 2 0 012 2v18H5V4a2 2 0 012-2zm2 5h6v2H9V7zm0 4h6v2H9v-2zm0 4h4v2H9v-2z" fill="currentColor" />
                </svg>
            );
        case 'survey':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H8l-4 3V6a2 2 0 012-2zm4 5h10v2H8V9zm0 4h7v2H8v-2z" fill="currentColor" />
                </svg>
            );
        case 'organization':
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M16 11a4 4 0 10-8 0 4 4 0 008 0zm-8 6a6 6 0 0116 0v3H8v-3zm-2 3H0v-3a6 6 0 0110-4.24A7.9 7.9 0 006 20z" fill="currentColor" />
                </svg>
            );
        default:
            return (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="8" fill="currentColor" />
                </svg>
            );
    }
}

function SearchCard({entity, t}) {
    const type = resolveEntityType(entity);
    const typeLabel = getEntityTypeLabel(type, t);
    const entityUrl = resolveEntityUrl(entity);
    const metaItems = getMetaItems(entity, t);
    const actions = Array.isArray(entity?.actions) ? entity.actions : [];

    return (
        <article className="am-card" data-kind={type}>
            <div className={`am-card__media am-media--${type}`} aria-hidden="true">
                <span className="am-card__icon">{getEntityIcon(type)}</span>
            </div>

            <div className="am-card__body">
                <div className="am-row">
                    <span className={`am-badge am-badge--${type}`}>{typeLabel}</span>
                    <span className="am-muted">{entity.dateAdded || ''}</span>
                </div>

                {entityUrl && isInternalUrl(entityUrl) && <Link className="am-title" to={entityUrl}>{entity.title}</Link>}
                {entityUrl && !isInternalUrl(entityUrl) && (
                    <a className="am-title" href={entityUrl} target="_blank" rel="noreferrer">
                        {entity.title}
                    </a>
                )}
                {!entityUrl && <span className="am-title">{entity.title}</span>}

                {!!entity.description && <p className="am-description">{entity.description}</p>}

                {!!metaItems.length && (
                    <div className="am-meta">
                        {metaItems.map((item, index) => (
                            <React.Fragment key={`${type}-meta-${index}`}>
                                {index > 0 && <span className="am-dot">•</span>}
                                <span>{item}</span>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {!!actions.length && (
                    <div className="am-actionsrow">
                        {actions.map((action, index) => {
                            const actionUrl = typeof action?.url === 'string' ? action.url : '';
                            if (!actionUrl) {
                                return null;
                            }

                            const actionKey = typeof action?.key === 'string' ? action.key : 'open';
                            const label = getActionLabel(type, actionKey, t);

                            if (isInternalUrl(actionUrl)) {
                                return (
                                    <Link key={`${type}-action-${index}`} className="am-link" to={actionUrl}>
                                        {label}
                                    </Link>
                                );
                            }

                            return (
                                <a
                                    key={`${type}-action-${index}`}
                                    className="am-link"
                                    href={actionUrl}
                                    target="_blank"
                                    rel="noreferrer">
                                    {label}
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </article>
    );
}

export default function SearchPage() {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const selectRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 10,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
    });
    const [facets, setFacets] = useState({types: {all: 0}});

    const [formSearch, setFormSearch] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);

    const [sortDraft, setSortDraft] = useState('relevance');
    const [dateFromDraft, setDateFromDraft] = useState('');
    const [dateToDraft, setDateToDraft] = useState('');
    const [withPointsDraft, setWithPointsDraft] = useState(false);
    const [onlineOnlyDraft, setOnlineOnlyDraft] = useState(false);
    const [availableOnlyDraft, setAvailableOnlyDraft] = useState(false);

    useMetaTags({
        title: t('search.title'),
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    const activeQuery = useMemo(() => ({...DEFAULT_QUERY, ...parseSearchQuery(location.search)}), [location.search]);

    useEffect(() => {
        setFormSearch(activeQuery.search);
        setSelectedType(activeQuery.type);
        setSortDraft(activeQuery.sort);
        setDateFromDraft(activeQuery.dateFrom);
        setDateToDraft(activeQuery.dateTo);
        setWithPointsDraft(activeQuery.withPointsOrCertificate);
        setOnlineOnlyDraft(activeQuery.onlineOnly);
        setAvailableOnlyDraft(activeQuery.availableOnly);
    }, [activeQuery]);

    useEffect(() => {
        function onDocumentClick(event) {
            if (!selectRef.current || selectRef.current.contains(event.target)) {
                return;
            }

            setIsTypeMenuOpen(false);
        }

        document.addEventListener('mousedown', onDocumentClick);
        return () => document.removeEventListener('mousedown', onDocumentClick);
    }, []);

    const fetchSearchResults = useCallback((query) => {
        const endpointParams = new URLSearchParams();

        if (query.search.trim() !== '') {
            endpointParams.set('search', query.search.trim());
        }

        endpointParams.set('type', query.type || 'all');
        endpointParams.set('page', String(query.page || 1));
        endpointParams.set('sort', query.sort || 'relevance');

        if (query.dateFrom) {
            endpointParams.set('dateFrom', query.dateFrom);
        }

        if (query.dateTo) {
            endpointParams.set('dateTo', query.dateTo);
        }

        if (query.withPointsOrCertificate) {
            endpointParams.set('withPointsOrCertificate', '1');
        }

        if (query.onlineOnly) {
            endpointParams.set('onlineOnly', '1');
        }

        if (query.availableOnly) {
            endpointParams.set('availableOnly', '1');
        }

        setIsLoading(true);

        fetch(`/api/v1/search?${endpointParams.toString()}`, {credentials: 'same-origin'})
            .then((response) => response.json().then((payload) => ({response, payload})))
            .then(({response, payload}) => {
                if (!response.ok || payload?.state !== 'active') {
                    setResults([]);
                    setPagination({page: query.page, perPage: 10, totalItems: 0, totalPages: 0, hasMore: false});
                    setFacets({types: {all: 0}});
                    return;
                }

                setResults(Array.isArray(payload.results) ? payload.results : []);
                setPagination(payload.pagination || {page: query.page, perPage: 10, totalItems: 0, totalPages: 0, hasMore: false});
                setFacets(payload.facets || {types: {all: payload?.pagination?.totalItems || 0}});
            })
            .catch(() => {
                setResults([]);
                setPagination({page: query.page, perPage: 10, totalItems: 0, totalPages: 0, hasMore: false});
                setFacets({types: {all: 0}});
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchSearchResults(activeQuery);
    }, [activeQuery, fetchSearchResults]);

    const updateRouteQuery = useCallback((nextQuery) => {
        const queryString = buildSearchQuery({...DEFAULT_QUERY, ...nextQuery});

        history.push({
            pathname: location.pathname,
            search: queryString ? `?${queryString}` : '',
        });
    }, [history, location.pathname]);

    const onSubmit = (event) => {
        event.preventDefault();

        updateRouteQuery({
            ...activeQuery,
            search: formSearch,
            type: selectedType,
            page: 1,
        });
    };

    const onTypeSelect = (nextType) => {
        setSelectedType(nextType);
        setIsTypeMenuOpen(false);

        updateRouteQuery({
            ...activeQuery,
            search: formSearch,
            type: nextType,
            page: 1,
        });
    };

    const onApplyFilters = () => {
        updateRouteQuery({
            ...activeQuery,
            search: formSearch,
            type: selectedType,
            page: 1,
            sort: sortDraft,
            dateFrom: dateFromDraft,
            dateTo: dateToDraft,
            withPointsOrCertificate: withPointsDraft,
            onlineOnly: onlineOnlyDraft,
            availableOnly: availableOnlyDraft,
        });
    };

    const onResetFilters = () => {
        setSortDraft('relevance');
        setDateFromDraft('');
        setDateToDraft('');
        setWithPointsDraft(false);
        setOnlineOnlyDraft(false);
        setAvailableOnlyDraft(false);

        updateRouteQuery({
            ...activeQuery,
            search: formSearch,
            type: selectedType,
            page: 1,
            sort: 'relevance',
            dateFrom: '',
            dateTo: '',
            withPointsOrCertificate: false,
            onlineOnly: false,
            availableOnly: false,
        });
    };

    const onPageClick = (nextPage) => {
        if (nextPage === activeQuery.page || nextPage < 1 || nextPage > (pagination.totalPages || 1)) {
            return;
        }

        updateRouteQuery({...activeQuery, page: nextPage});
    };

    const hasNotFoundState = !isLoading && activeQuery.search.trim() !== '' && results.length === 0;
    const paginationItems = getPaginationItems(pagination.page || 1, pagination.totalPages || 0);

    const selectedTypeOption = TYPE_OPTIONS.find((option) => option.value === selectedType) || TYPE_OPTIONS[0];

    return (
        <section className="am-search-page">
            <div className="container">
                <AdvertiseBlock unitId="search1" blockLocation="top" />

                <div className="am-page">
                    <div className="am-searchbar" aria-label={t('search.title')}>
                        <form className="am-searchbar__form" onSubmit={onSubmit} role="search">
                            <div className="am-field">
                                <svg className="am-ic" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" fill="currentColor" />
                                </svg>
                                <input
                                    name="search"
                                    type="search"
                                    value={formSearch}
                                    placeholder={t('search.inputPlaceholder', 'Пошук…')}
                                    aria-label={t('search.queryAria', 'Запит')}
                                    onChange={(event) => setFormSearch(event.target.value)}
                                />
                            </div>

                            <div className="am-select" aria-label={t('search.typeAria', 'Тип')} ref={selectRef}>
                                <button
                                    className="am-select__btn"
                                    type="button"
                                    aria-haspopup="listbox"
                                    aria-expanded={isTypeMenuOpen}
                                    onClick={() => setIsTypeMenuOpen((current) => !current)}>
                                    <span>{t(selectedTypeOption.labelKey)}</span>
                                    <svg viewBox="0 0 20 20" aria-hidden="true">
                                        <path d="M5.5 7.5L10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                                <div className="am-select__menu" role="listbox" hidden={!isTypeMenuOpen}>
                                    {TYPE_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            role="option"
                                            aria-selected={selectedType === option.value}
                                            className={`am-select__opt ${selectedType === option.value ? 'is-active' : ''}`}
                                            type="button"
                                            onClick={() => onTypeSelect(option.value)}>
                                            {t(option.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button className="am-btn am-btn--primary" type="submit">
                                {t('search.submit')}
                            </button>

                            <div className="am-searchbar__meta" aria-label={t('search.metaAria', 'Мета')}>
                                <span className="am-muted">{t('search.totalResultsLabel', 'Результатів:')}</span>
                                <strong>{pagination.totalItems || 0}</strong>
                            </div>
                        </form>

                        <div className="am-chips" aria-label={t('search.quickFiltersAria', 'Швидкі фільтри')}>
                            {TYPE_OPTIONS.map((option) => (
                                <button
                                    key={`chip-${option.value}`}
                                    className={`am-chip ${selectedType === option.value ? 'is-active' : ''}`}
                                    type="button"
                                    onClick={() => onTypeSelect(option.value)}>
                                    {t(option.labelKey)}
                                    <span className="am-chip__count">{facets?.types?.[option.value] ?? 0}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <main className="am-layout" aria-label={t('search.resultsAria', 'Видача')}>
                        <div className="am-results">
                            {hasNotFoundState && (
                                <div className="result-not-found">
                                    <h3 dangerouslySetInnerHTML={{__html: t('search.notFound', {search: activeQuery.search})}}></h3>
                                    <div dangerouslySetInnerHTML={{__html: t('search.hintsOnNotFound')}}></div>
                                </div>
                            )}

                            {!hasNotFoundState && results.map((entity, index) => (
                                <React.Fragment key={`${entity.type}-${entity.id}`}>
                                    {index === 4 && <AdvertiseBlock unitId="search2" blockLocation="list" />}
                                    <SearchCard entity={entity} t={t} />
                                </React.Fragment>
                            ))}

                            {(pagination.totalPages || 0) > 1 && (
                                <div className="am-pager" aria-label={t('search.paginationAria', 'Пагінація')}>
                                    <button className="am-btn" type="button" disabled={pagination.page <= 1} onClick={() => onPageClick((pagination.page || 1) - 1)}>
                                        {t('search.prev', 'Назад')}
                                    </button>
                                    {paginationItems.map((item) => {
                                        if (typeof item !== 'number') {
                                            return <span key={item} className="am-muted">…</span>;
                                        }

                                        return (
                                            <button
                                                key={`page-${item}`}
                                                className={`am-btn ${item === pagination.page ? 'is-active' : ''}`}
                                                type="button"
                                                onClick={() => onPageClick(item)}>
                                                {item}
                                            </button>
                                        );
                                    })}
                                    <button
                                        className="am-btn"
                                        type="button"
                                        disabled={pagination.page >= (pagination.totalPages || 1)}
                                        onClick={() => onPageClick((pagination.page || 1) + 1)}>
                                        {t('search.next', 'Далі')}
                                    </button>
                                </div>
                            )}
                        </div>

                        <aside className="am-filters" aria-label={t('search.filtersAria', 'Фільтри')}>
                            <div className="am-filters__card">
                                <div className="am-filters__title">{t('search.filters.title', 'Фільтри')}</div>

                                <div className="am-field2">
                                    <label className="am-field2__label" htmlFor="search-sort-select">{t('search.filters.sort', 'Сортування')}</label>
                                    <select
                                        id="search-sort-select"
                                        value={sortDraft}
                                        onChange={(event) => setSortDraft(event.target.value)}>
                                        {SORT_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="am-field2">
                                    <div className="am-field2__label">{t('search.filters.date', 'Дата')}</div>
                                    <div className="am-range">
                                        <input type="date" value={dateFromDraft} onChange={(event) => setDateFromDraft(event.target.value)} />
                                        <span className="am-muted">—</span>
                                        <input type="date" value={dateToDraft} onChange={(event) => setDateToDraft(event.target.value)} />
                                    </div>
                                </div>

                                <div className="am-field2">
                                    <div className="am-field2__label">{t('search.filters.additional', 'Додатково')}</div>
                                    <label className="am-check">
                                        <input type="checkbox" checked={withPointsDraft} onChange={(event) => setWithPointsDraft(event.target.checked)} />
                                        {t('search.filters.withPointsOrCertificate', 'З балами/сертифікатом')}
                                    </label>
                                    <label className="am-check">
                                        <input type="checkbox" checked={onlineOnlyDraft} onChange={(event) => setOnlineOnlyDraft(event.target.checked)} />
                                        {t('search.filters.onlineOnly', 'Тільки онлайн')}
                                    </label>
                                    <label className="am-check">
                                        <input type="checkbox" checked={availableOnlyDraft} onChange={(event) => setAvailableOnlyDraft(event.target.checked)} />
                                        {t('search.filters.availableOnly', 'Тільки доступні мені')}
                                    </label>
                                </div>

                                <button className="am-btn am-btn--primary am-btn--full" type="button" onClick={onApplyFilters}>
                                    {t('search.filters.apply', 'Застосувати')}
                                </button>
                                <button className="am-btn am-btn--ghost am-btn--full" type="button" onClick={onResetFilters}>
                                    {t('search.filters.reset', 'Скинути')}
                                </button>

                                <div className="am-search-sidebar-ad">
                                    <AdvertiseBlock unitId="search3" blockLocation="sidebar" />
                                </div>
                            </div>
                        </aside>
                    </main>
                </div>
            </div>
        </section>
    );
}
